export type FairIconSvgMode = 'monochrome' | 'brand'

export function prepareIconSvg(svg: string, mode: FairIconSvgMode): string {
  if (mode === 'brand') {
    return svg
      .replace(/fill="var\(--fill-\d+,\s*([^"]+)\)"/g, 'fill="$1"')
      .replace(/stroke="var\(--stroke-\d+,\s*([^"]+)\)"/g, 'stroke="$1"')
      .trim()
  }

  return svg
    .replace(/preserveAspectRatio="none"/g, 'preserveAspectRatio="xMidYMid meet"')
    .replace(/\swidth="100%"/g, '')
    .replace(/\sheight="100%"/g, '')
    .replace(/\soverflow="visible"/g, '')
    .replace(/\sstyle="display:\s*block;"/g, '')
    .replace(/fill="var\(--fill-\d+,\s*[^"]*\)"/g, 'fill="currentColor"')
    .replace(/stroke="var\(--stroke-\d+,\s*[^"]*\)"/g, 'stroke="currentColor"')
    .replace(/fill="white"/g, 'fill="currentColor"')
    .replace(/stroke="white"/g, 'stroke="currentColor"')
    .replace(/fill="black"/g, 'fill="currentColor"')
    .replace(/stroke="black"/g, 'stroke="currentColor"')
    .replace(/fill="#1[Aa]2133"/g, 'fill="currentColor"')
    .replace(/fill="#000(?:000)?"/g, 'fill="currentColor"')
    .trim()
}

/**
 * FE-017 — split an icon's single `<path>` into independently animatable parts.
 *
 * Most glyphs in `src/ui/icons/main/` are one `<path>` whose `d` holds several
 * subpaths (each starting with `M`). A subpath drawn in the opposite winding is
 * a hole (the inner outline of a house, the eye of a needle), so it must stay
 * in the same `<path>` as its outer contour — hence `parts` is a list of
 * subpath-index groups, not a flat split. Each group becomes its own `<path>`
 * with `class="fair-icon__part fair-icon__part--<n>"` for CSS to move.
 *
 * `fill-rule="evenodd"` paths are left whole: their shapes subtract from one
 * another and would fall apart. Unknown indices are ignored; subpaths not
 * listed in any group are kept together in a trailing part.
 */
export function splitIconSvgParts(svg: string, parts: number[][]): string {
  const match = /<path\b([^>]*?)\sd="([^"]*)"([^>]*)\/?>/.exec(svg)
  if (!match) return svg
  const [full, before, d, after] = match
  const attrs = `${before}${after}`
  if (/fill-rule="evenodd"/.test(attrs)) return svg

  const subpaths = d.match(/[Mm][^Mm]*/g) ?? []
  if (subpaths.length < 2) return svg

  const used = new Set<number>()
  const groups = parts
    .map((group) => group.filter((i) => i >= 0 && i < subpaths.length && !used.has(i) && (used.add(i), true)))
    .filter((group) => group.length > 0)
  const rest = subpaths.map((_, i) => i).filter((i) => !used.has(i))
  if (rest.length) groups.push(rest)

  const cleanAttrs = attrs.replace(/\sid="[^"]*"/, '').replace(/\sclass="[^"]*"/, '')
  const paths = groups
    .map((group, n) =>
      `<path class="fair-icon__part fair-icon__part--${n}" data-part="${n}" d="${group.map((i) => subpaths[i].trim()).join(' ')}"${cleanAttrs}/>`,
    )
    .join('')
  return svg.replace(full, paths)
}
